/**
 * Number display (design fix 2, web.md §8): counts are content-sized; from 10,000 they render compact
 * (`12.4k`, `100k`, `1.2M`) with the exact value in the tooltip; the icon rail caps badges at `99+`.
 */

export const COMPACT_FROM = 10_000;

const exact = new Intl.NumberFormat('en', { maximumFractionDigits: 0 });

export function formatExact(n: number): string {
  return exact.format(n);
}

/** `9999` → "9,999"; `12400` → "12.4k"; `100000` → "100k"; `1234567` → "1.2M". */
export function formatCount(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs < COMPACT_FROM) return formatExact(n);
  const [value, unit] = abs >= 1e9 ? [n / 1e9, 'B'] : abs >= 1e6 ? [n / 1e6, 'M'] : [n / 1e3, 'k'];
  const rounded = Math.abs(value) >= 100 ? Math.floor(value) : Math.floor(value * 10) / 10;
  return `${rounded.toFixed(Math.abs(rounded) >= 100 || Number.isInteger(rounded) ? 0 : 1)}${unit}`;
}

/** True when the shown text is not the exact number (a tooltip is then required). */
export function isCompacted(n: number): boolean {
  return Number.isFinite(n) && Math.abs(n) >= COMPACT_FROM;
}

/** Icon-rail badge: up to 99, then "99+". */
export function formatBadge(n: number): string {
  return n > 99 ? '99+' : String(n);
}

/** "3 min", "1 h 05 min". */
export function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, '0')} min`;
}
